/**
 * parentCategories — the product catalog's parent SKU -> product line, embedded.
 *
 * WHY IT IS IN THE CODE AND NOT READ AT RUNTIME. Category used to come from the
 * OPERATIONS Masterfile, a SharePoint list on a third site: one more network
 * read on the critical path of every import, one more thing to be unreachable,
 * and unreadable to the parse worker and to the hub's VBA at all. It is also
 * barely a hundred lines of near-static fact — 135 parents, one line each,
 * which changes when a new design is launched and at no other time.
 *
 * So it is embedded, from Masterlist_Reference.xlsb (Mikko, 2026-09-07: 135
 * rows, every parent unique, no two rows disagreeing about a category). The
 * SAME table is embedded in automation/bqc-auto-refresh/AutoFillCosting.bas so
 * the hub's sheets and this app classify a bundle identically; changing one
 * without the other is what makes a screen disagree with the workbook it was
 * built from.
 *
 * KEYED BY PARENT, not by SKU CODE. The catalog names SS028; the floor writes
 * SS028-MDBLU-L. Keying on the parent is what makes one line cover every
 * colour and size of a design, and it is why this table is 135 rows rather
 * than thousands.
 *
 * LAB GOWN IS NOT AN iSyn_WIPs CATEGORY. LG01 and LG02 are carried here
 * because costing groups by whatever the catalog says, but `CATEGORIES` in
 * lib/sp/categories.ts — which is also the SharePoint Choice column — has no
 * such value, and masterCategory.ts refuses to write a category that is not on
 * that list. Adding LAB GOWN there is a provisioning change (the Choice column
 * has to learn it first), not an edit to this file.
 *
 * ADDING A DESIGN: one line here, one line in AutoFillCosting.bas, and a row
 * on the hub's COGM sheet if it should also be priced at its own rate rather
 * than at its category's average.
 *
 * Zero-import on purpose: the parse worker takes it, and so does every test.
 */

/** Parent SKU (upper-case) -> the product line the catalog files it under. */
export const PARENT_CATEGORY: Readonly<Record<string, string>> = {
  // ELITE — 25
  SS028: 'ELITE', SS029: 'ELITE', SS030: 'ELITE', SS031: 'ELITE', SS031A: 'ELITE',
  SS031B: 'ELITE', SS032: 'ELITE', SS033: 'ELITE', SS034: 'ELITE', SS035: 'ELITE',
  SS036: 'ELITE', SS037: 'ELITE', SS038: 'ELITE', SS039: 'ELITE', SS040: 'ELITE',
  SS041: 'ELITE', SS042: 'ELITE', SS043: 'ELITE', SS044: 'ELITE', SS045: 'ELITE',
  SS046: 'ELITE', SS047: 'ELITE', SS048: 'ELITE', SS049: 'ELITE', SS050: 'ELITE',
  // ALTA — 12
  SS051: 'ALTA', SS052: 'ALTA', SS053: 'ALTA', SS054: 'ALTA', SS055: 'ALTA', SS056: 'ALTA',
  SS057: 'ALTA', SS058: 'ALTA', SS059: 'ALTA', SS062: 'ALTA', SS063: 'ALTA', SS064: 'ALTA',
  // CLASSIC — 68
  SS01A: 'CLASSIC', SS01B: 'CLASSIC', SS01C: 'CLASSIC', SS01D: 'CLASSIC', SS01I: 'CLASSIC',
  SS02: 'CLASSIC', SS03: 'CLASSIC', SS03A: 'CLASSIC', SS03B: 'CLASSIC', SS03C: 'CLASSIC',
  SS03C1: 'CLASSIC', SS03D: 'CLASSIC', SS03E: 'CLASSIC', SS03F: 'CLASSIC', SS03H: 'CLASSIC',
  SS03I: 'CLASSIC', SS03J: 'CLASSIC', SS04: 'CLASSIC', SS04A: 'CLASSIC', SS04B: 'CLASSIC',
  SS04C: 'CLASSIC', SS04D: 'CLASSIC', SS04H: 'CLASSIC', SS04I: 'CLASSIC', SS04J: 'CLASSIC',
  SS05: 'CLASSIC', SS05A: 'CLASSIC', SS05C: 'CLASSIC', SS06: 'CLASSIC', SS06A: 'CLASSIC',
  SS06B: 'CLASSIC', SS06C: 'CLASSIC', SS07A: 'CLASSIC', SS08: 'CLASSIC', SS08A: 'CLASSIC',
  SS08B: 'CLASSIC', SS09: 'CLASSIC', SS09B: 'CLASSIC', SS09D: 'CLASSIC', SS09E: 'CLASSIC',
  SS010: 'CLASSIC', SS010A: 'CLASSIC', SS011: 'CLASSIC', SS012: 'CLASSIC', SS013: 'CLASSIC',
  SS014: 'CLASSIC', SS015: 'CLASSIC', SS015A: 'CLASSIC', SS015B: 'CLASSIC', SS016: 'CLASSIC',
  SS017: 'CLASSIC', SS018: 'CLASSIC', SS018A: 'CLASSIC', SS019: 'CLASSIC', SS019A: 'CLASSIC',
  SS019B: 'CLASSIC', SS020: 'CLASSIC', SS020A: 'CLASSIC', SS021: 'CLASSIC', SS022: 'CLASSIC',
  SS023: 'CLASSIC', SS024: 'CLASSIC', SS024A: 'CLASSIC', SS024B: 'CLASSIC', SS024C: 'CLASSIC',
  SS025: 'CLASSIC', SS026: 'CLASSIC', SS027: 'CLASSIC',
  // DOCTORS COAT — 6
  DC02: 'DOCTORS COAT', DC01: 'DOCTORS COAT', DC03: 'DOCTORS COAT', DC04: 'DOCTORS COAT',
  DC05: 'DOCTORS COAT', DC06: 'DOCTORS COAT',
  // ELITE PRO — 11
  SS080: 'ELITE PRO', SS081: 'ELITE PRO', SS082: 'ELITE PRO', SS083: 'ELITE PRO',
  SS084: 'ELITE PRO', SS085: 'ELITE PRO', SS086: 'ELITE PRO', SS087: 'ELITE PRO',
  SS088: 'ELITE PRO', SS089: 'ELITE PRO', SS090: 'ELITE PRO',
  // LAB GOWN — 2
  LG01: 'LAB GOWN', LG02: 'LAB GOWN',
  // NURSE UNIFORM — 6
  WU01: 'NURSE UNIFORM', WU02: 'NURSE UNIFORM', WU03: 'NURSE UNIFORM', WU04: 'NURSE UNIFORM',
  WU05: 'NURSE UNIFORM', WU06: 'NURSE UNIFORM',
  // PRIMO — 5
  SS070: 'PRIMO', SS075: 'PRIMO', SS074: 'PRIMO', SS072: 'PRIMO', SS073: 'PRIMO',
};

/**
 * The catalog's category for a parent SKU, or '' when it lists none.
 *
 * Case- and space-insensitive on the way in, because the caller may be handing
 * over a DESIGN cell somebody typed. Returns '' rather than a guess: an
 * unknown design is a question for whoever maintains the catalog, and a
 * fallback here would answer it wrongly and silently.
 */
export function categoryOfParent(parent: string | undefined | null): string {
  if (!parent) return '';
  return PARENT_CATEGORY[parent.trim().toUpperCase()] ?? '';
}

/** How many parents the embedded catalog knows — for a screen that wants to
 * say what its classification rests on. */
export const PARENT_CATEGORY_COUNT = Object.keys(PARENT_CATEGORY).length;
